import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase, CheckCircle, ChevronLeft, Circle, FileText, GraduationCap,
  ImageIcon, Loader2, MapPin, MessageCircle, Phone, Save, Search, Shield,
  TrendingUp, Upload, UserRound,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import PremiumUpgradeCard from '../../components/PremiumUpgradeCard';
import { toast } from 'sonner';

const STATES = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const HEALTH_PLAN_OPTIONS = ['Particular', 'Hapvida', 'Bradesco', 'SulAmérica', 'Unimed', 'NotreDame', 'Amil', 'Outros'];
const API_URL = process.env.REACT_APP_BACKEND_URL;

function normalizeHealthPlans(plans) {
  const values = Array.isArray(plans) ? plans.filter(Boolean) : [];
  return ['Particular', ...values.filter((plan) => plan !== 'Particular')];
}

function isPremiumProfile(profile) {
  return ['premium', 'enterprise'].includes(String(profile?.subscription_plan || '').toLowerCase());
}

function photoUrl(photo) {
  if (!photo) return '';
  return photo.startsWith('http') ? photo : `${API_URL}${photo}`;
}

function profilePhoto(profile) {
  return profile?.logo || profile?.photo || '';
}

function specialtyFromProfile(profile) {
  const direct = profile?.specialty_name || '';
  if (direct) return direct;
  const match = String(profile?.description || '').match(/^Especialidade:\s*(.+)$/i);
  return match?.[1] || '';
}

function statusLabel(status) {
  const labels = {
    nao_informado: 'Não informado',
    formato_invalido: 'Formato inválido',
    informado: 'Informado',
    pendente_verificacao: 'Em verificação',
    verificado: 'Verificado',
    divergente: 'Divergente',
    revisao_manual: 'Revisão manual',
    nao_verificado: 'Não verificada',
    email_confirmado: 'Email confirmado',
    telefone_confirmado: 'Telefone confirmado',
    documento_confirmado: 'Documento confirmado',
  };
  return labels[status] || 'Não informado';
}

function statusBadgeClass(status) {
  if (status === 'verificado' || status === 'documento_confirmado') {
    return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100';
  }
  if (status === 'informado' || status === 'pendente_verificacao' || status === 'email_confirmado' || status === 'telefone_confirmado') {
    return 'bg-blue-100 text-blue-700 hover:bg-blue-100';
  }
  if (status === 'formato_invalido' || status === 'divergente') {
    return 'bg-red-100 text-red-700 hover:bg-red-100';
  }
  return 'bg-slate-100 text-slate-600 hover:bg-slate-100';
}

function mapClaimedDoctorToProfile(doctor, fallbackProfile = {}) {
  if (!doctor) return fallbackProfile;
  const primaryAddress = Array.isArray(doctor.addresses)
    ? doctor.addresses.find((item) => item.is_primary) || doctor.addresses[0]
    : null;

  return {
    ...fallbackProfile,
    ...doctor,
    trade_name: doctor.full_name || fallbackProfile.trade_name || '',
    legal_name: doctor.legal_name || doctor.full_name || fallbackProfile.legal_name || '',
    description: doctor.bio || '',
    address: doctor.address || primaryAddress?.address || '',
    neighborhood: doctor.neighborhood || primaryAddress?.neighborhood || '',
    city: doctor.city || primaryAddress?.city || '',
    state: doctor.state || primaryAddress?.state || '',
    zip_code: doctor.zip_code || primaryAddress?.zip_code || '',
    phone: doctor.phone || primaryAddress?.phone || '',
    logo: doctor.photo || '',
    claimed_cnpj_id: doctor.id || fallbackProfile.claimed_cnpj_id,
  };
}

function calcCompleteness(data, profile) {
  const items = [
    { key: 'photo', label: 'Foto do perfil', done: !!profilePhoto(profile) },
    { key: 'name', label: 'Nome público preenchido', done: !!data.trade_name?.trim() },
    { key: 'specialty', label: 'Especialidade definida', done: !!data.specialty_name?.trim() },
    { key: 'address', label: 'Endereço completo com CEP', done: !!(data.address?.trim() && data.neighborhood?.trim() && data.city?.trim() && data.state && data.zip_code?.trim()) },
    { key: 'contact', label: 'Telefone de contato', done: !!data.phone?.trim() },
    { key: 'formation', label: 'Formação e experiência', done: !!(data.formacao?.trim() && data.experiencia?.trim()) },
    { key: 'description', label: 'Apresentação profissional', done: !!data.description?.trim() },
    { key: 'verified', label: 'CNPJ vinculado', done: !!profile?.cnpj },
  ];
  const done = items.filter((item) => item.done).length;
  return { percent: Math.round((done / items.length) * 100), items };
}

export default function ProviderProfile() {
  const { api, profile, refreshUser } = useAuth();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [claimedDoctor, setClaimedDoctor] = useState(null);
  const [providerDetails, setProviderDetails] = useState(null);
  const [profileMode, setProfileMode] = useState(null);
  const [formData, setFormData] = useState({
    trade_name: '',
    specialty_id: '',
    specialty_name: '',
    address: '',
    neighborhood: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    responsible_name: '',
    description: '',
    formacao: '',
    experiencia: '',
    accepted_health_plans: ['Particular'],
  });

  const isClaimedDoctor = profileMode === 'claimed';
  const effectiveProfile = useMemo(
    () => (isClaimedDoctor ? mapClaimedDoctorToProfile(claimedDoctor, profile) : providerDetails || profile),
    [claimedDoctor, isClaimedDoctor, profile, providerDetails]
  );

  useEffect(() => {
    const fetchProfile = async () => {
      setProfileLoading(true);
      try {
        const { data } = await api.get('/provider/me/doctor');
        setClaimedDoctor(data);
        setProviderDetails(null);
        setProfileMode('claimed');
      } catch (error) {
        try {
          const { data } = await api.get('/provider/profile');
          setProviderDetails(data);
          setClaimedDoctor(null);
          setProfileMode('provider');
        } catch (providerError) {
          toast.error(providerError.response?.data?.detail || 'Erro ao carregar perfil profissional');
        }
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!effectiveProfile) return;
    setFormData({
      trade_name: effectiveProfile.trade_name || '',
      specialty_id: effectiveProfile.specialty_id || '',
      specialty_name: specialtyFromProfile(effectiveProfile),
      address: effectiveProfile.address || '',
      neighborhood: effectiveProfile.neighborhood || '',
      city: effectiveProfile.city || '',
      state: effectiveProfile.state || '',
      zip_code: effectiveProfile.zip_code || '',
      phone: effectiveProfile.phone || '',
      responsible_name: effectiveProfile.responsible_name || '',
      description: effectiveProfile.description || '',
      formacao: effectiveProfile.formacao || '',
      experiencia: effectiveProfile.experiencia || '',
      accepted_health_plans: normalizeHealthPlans(effectiveProfile.accepted_health_plans),
    });
  }, [effectiveProfile]);

  const premium = isClaimedDoctor ? !!effectiveProfile?.is_premium : isPremiumProfile(effectiveProfile);
  const completeness = useMemo(() => calcCompleteness(formData, effectiveProfile), [formData, effectiveProfile]);

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const toggleHealthPlan = (plan) => {
    if (plan === 'Particular') return;
    setFormData((current) => {
      const currentPlans = normalizeHealthPlans(current.accepted_health_plans);
      const nextPlans = currentPlans.includes(plan)
        ? currentPlans.filter((item) => item !== plan)
        : [...currentPlans, plan];
      return { ...current, accepted_health_plans: normalizeHealthPlans(nextPlans) };
    });
  };

  const formatCEP = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1');
  };

  const lookupCep = async (raw) => {
    const digits = raw.replace(/\D/g, '');
    if (digits.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data?.erro) {
        toast.error('CEP não encontrado');
        return;
      }
      setFormData((current) => ({
        ...current,
        zip_code: formatCEP(digits),
        state: data.uf || current.state,
        city: data.localidade || current.city,
        neighborhood: data.bairro || current.neighborhood,
        address: data.logradouro || current.address,
      }));
    } catch {
      toast.error('Falha ao buscar CEP.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isClaimedDoctor) {
        await api.patch('/provider/me/doctor', {
          full_name: formData.trade_name,
          specialty_id: formData.specialty_id,
          specialty_name: formData.specialty_name,
          address: formData.address,
          neighborhood: formData.neighborhood,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          phone: formData.phone,
          whatsapp: formData.phone,
          bio: formData.description,
          formacao: formData.formacao,
          experiencia: formData.experiencia,
          accepted_health_plans: normalizeHealthPlans(formData.accepted_health_plans),
        });
        const { data: freshDoctor } = await api.get('/provider/me/doctor');
        setClaimedDoctor(freshDoctor);
      } else {
        await api.put('/provider/profile', {
          ...formData,
          accepted_health_plans: normalizeHealthPlans(formData.accepted_health_plans),
        });
        const { data: freshProvider } = await api.get('/provider/profile');
        setProviderDetails(freshProvider);
      }
      toast.success('Perfil atualizado com sucesso');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Use JPG, PNG ou WEBP');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Arquivo maior que 4 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (isClaimedDoctor) {
        await api.post('/provider/me/doctor/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const { data } = await api.get('/provider/me/doctor');
        setClaimedDoctor(data);
      } else {
        await api.post('/provider/profile/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const { data } = await api.get('/provider/profile');
        setProviderDetails(data);
      }
      toast.success('Foto enviada');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar foto');
    } finally {
      setUploading(false);
    }
  };

  const startPremiumCheckout = async (plan = 'monthly') => {
    try {
      const response = await api.post(
        isClaimedDoctor ? '/provider/me/doctor/premium-checkout' : '/provider/profile/premium-checkout',
        { plan }
      );
      const checkoutUrl = response.data?.url;
      if (!checkoutUrl) {
        toast.error('Checkout indispon?vel no momento');
        return null;
      }
      window.location.assign(checkoutUrl);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao abrir checkout do Stripe');
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F7FAF9]" data-testid="provider-profile">
      <Navbar />

      <main className="container-app py-8">
        <div className="mx-auto max-w-6xl">
          <Link to="/" className="mb-5 inline-flex items-center gap-2 text-sm text-slate-600 transition-colors hover:text-primary">
            <ChevronLeft className="h-4 w-4" />
            Voltar
          </Link>

          <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Perfil profissional</p>
              <h1 className="mt-1 text-2xl font-bold text-slate-950 md:text-3xl">Edite como o paciente verá seu perfil</h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
                Complete as informações públicas, aumente a confiança do paciente e veja ao lado o que fica liberado no Premium.
              </p>
            </div>
            <Badge className={premium ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-700 hover:bg-slate-100'}>
              {premium ? 'Premium ativo' : 'Plano gratuito'}
            </Badge>
          </div>

          {profileLoading && (
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm text-slate-600 shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              Carregando dados do perfil reivindicado...
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-[1fr_340px] lg:items-start">
            <div className="space-y-6">
              <PublicPreview formData={formData} profile={effectiveProfile} premium={premium} completeness={completeness} />

              <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm md:p-6" data-testid="provider-verification-status">
                <h2 className="flex items-center gap-2 font-semibold text-slate-950">
                  <Shield className="h-5 w-5 text-primary" />
                  Status de confiança
                </h2>
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Registro profissional</p>
                    <Badge className={`mt-2 ${statusBadgeClass(effectiveProfile?.professional_registration_status)}`}>
                      {statusLabel(effectiveProfile?.professional_registration_status)}
                    </Badge>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">CNPJ</p>
                    <Badge className={`mt-2 ${statusBadgeClass(effectiveProfile?.cnpj_status)}`}>
                      {statusLabel(effectiveProfile?.cnpj_status)}
                    </Badge>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Identidade da conta</p>
                    <Badge className={`mt-2 ${statusBadgeClass(effectiveProfile?.identity_status)}`}>
                      {statusLabel(effectiveProfile?.identity_status)}
                    </Badge>
                  </div>
                </div>
              </section>

              <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm md:p-6">
                <div className="mb-5">
                  <h2 className="flex items-center gap-2 font-semibold text-slate-950">
                    <FileText className="h-5 w-5 text-primary" />
                    Informações do perfil público
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">Esses dados ajudam o paciente a entender quem você é e como entrar em contato.</p>
                </div>

                <div className="space-y-6">
                  <section>
                    <h3 className="mb-4 flex items-center gap-2 font-semibold text-slate-950">
                      <ImageIcon className="h-5 w-5 text-primary" />
                      Foto e especialidade
                    </h3>
                    <div className="grid gap-4 md:grid-cols-[auto_1fr] md:items-end">
                      <div className="flex items-center gap-4">
                        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 text-slate-300">
                          {profilePhoto(effectiveProfile) ? (
                            <img src={photoUrl(profilePhoto(effectiveProfile))} alt={formData.trade_name || 'Foto do perfil'} className="h-full w-full object-cover" />
                          ) : (
                            <UserRound className="h-9 w-9" />
                          )}
                        </div>
                        <div>
                          <input
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => uploadPhoto(e.target.files?.[0])}
                          />
                          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                            {profilePhoto(effectiveProfile) ? 'Trocar foto' : 'Enviar foto'}
                          </Button>
                          <p className="mt-1 text-xs text-slate-500">JPG, PNG ou WEBP até 4 MB.</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="specialty_name">Especialidade</Label>
                        <Input
                          id="specialty_name"
                          value={formData.specialty_name}
                          readOnly
                          className="bg-slate-50 text-slate-600"
                          placeholder="Definida no cadastro"
                        />
                        <p className="text-xs text-slate-500">A especialidade vem do cadastro inicial para manter consistência no perfil público.</p>
                      </div>
                    </div>
                  </section>

                  <section className="grid gap-4 border-t border-slate-100 pt-6 md:grid-cols-2">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="trade_name">Nome público</Label>
                      <Input
                        id="trade_name"
                        value={formData.trade_name}
                        onChange={(e) => updateField('trade_name', e.target.value)}
                        placeholder="Nome do profissional, consultório ou clínica"
                        data-testid="provider-trade-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Razão social</Label>
                      <Input value={effectiveProfile?.legal_name || ''} disabled className="bg-slate-50 text-slate-500" />
                    </div>
                    <div className="space-y-2">
                      <Label>CNPJ</Label>
                      <Input value={effectiveProfile?.cnpj || ''} disabled className="bg-slate-50 text-slate-500" />
                    </div>
                  </section>

                  <section className="border-t border-slate-100 pt-6">
                    <h3 className="mb-4 flex items-center gap-2 font-semibold text-slate-950">
                      <MapPin className="h-5 w-5 text-primary" />
                      Local de atendimento
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="zip_code">CEP de atendimento</Label>
                        <Input
                          id="zip_code"
                          value={formData.zip_code}
                          onChange={(e) => {
                            const masked = formatCEP(e.target.value);
                            updateField('zip_code', masked);
                            if (masked.replace(/\D/g, '').length === 8) lookupCep(masked);
                          }}
                          onBlur={(e) => lookupCep(e.target.value)}
                          placeholder="00000-000"
                          maxLength={9}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">Estado</Label>
                        <Select value={formData.state} onValueChange={(value) => updateField('state', value)}>
                          <SelectTrigger id="state">
                            <SelectValue placeholder="UF" />
                          </SelectTrigger>
                          <SelectContent>
                            {STATES.map((uf) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="address">Endereço</Label>
                        <Input
                          id="address"
                          value={formData.address}
                          onChange={(e) => updateField('address', e.target.value)}
                          placeholder="Rua, número, complemento"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="neighborhood">Bairro</Label>
                        <Input id="neighborhood" value={formData.neighborhood} onChange={(e) => updateField('neighborhood', e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="city">Cidade</Label>
                        <Input id="city" value={formData.city} onChange={(e) => updateField('city', e.target.value)} />
                      </div>
                    </div>
                  </section>

                  <section className="border-t border-slate-100 pt-6">
                    <h3 className="mb-4 flex items-center gap-2 font-semibold text-slate-950">
                      <Phone className="h-5 w-5 text-primary" />
                      Contato e apresentação
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefone</Label>
                        <Input id="phone" value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="(00) 00000-0000" />
                        {!premium && (
                          <p className="text-xs text-slate-500">O botão de WhatsApp no perfil público é liberado no Premium.</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="responsible">Responsável</Label>
                        <Input id="responsible" value={formData.responsible_name} onChange={(e) => updateField('responsible_name', e.target.value)} />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="description">Sobre o profissional ou clínica</Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => updateField('description', e.target.value)}
                          rows={5}
                          placeholder="Conte sua área de atuação, diferenciais, estrutura de atendimento e o que o paciente deve saber antes de escolher você."
                        />
                      </div>
                    </div>
                  </section>

                  <section className="border-t border-slate-100 pt-6">
                    <h3 className="mb-4 flex items-center gap-2 font-semibold text-slate-950">
                      <Shield className="h-5 w-5 text-primary" />
                      Planos atendidos
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {HEALTH_PLAN_OPTIONS.map((plan) => {
                        const checked = normalizeHealthPlans(formData.accepted_health_plans).includes(plan);
                        const locked = plan === 'Particular';
                        return (
                          <label
                            key={plan}
                            className={`flex min-h-11 items-center gap-3 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                              checked
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={locked}
                              onChange={() => toggleHealthPlan(plan)}
                              className="h-4 w-4 accent-primary"
                            />
                            <span>{plan}</span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">Atendimento particular fica sempre ativo. Selecione também os convênios aceitos.</p>
                  </section>

                  <section className="border-t border-slate-100 pt-6">
                    <h3 className="mb-4 flex items-center gap-2 font-semibold text-slate-950">
                      <GraduationCap className="h-5 w-5 text-primary" />
                      Formação e experiência
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="formacao">Formação</Label>
                        <Textarea
                          id="formacao"
                          value={formData.formacao}
                          onChange={(e) => updateField('formacao', e.target.value)}
                          rows={4}
                          placeholder="Ex: Graduação, residência, especialização, certificações..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="experiencia">Experiência</Label>
                        <Textarea
                          id="experiencia"
                          value={formData.experiencia}
                          onChange={(e) => updateField('experiencia', e.target.value)}
                          rows={4}
                          placeholder="Ex: áreas de atuação, hospitais, anos de prática, linhas de cuidado..."
                        />
                      </div>
                    </div>
                  </section>

                  <div className="flex justify-end border-t border-slate-100 pt-5">
                    <Button type="submit" className="bg-primary" disabled={loading} data-testid="save-provider-btn">
                      <Save className="mr-2 h-4 w-4" />
                      {loading ? 'Salvando...' : 'Salvar alterações'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>

            <aside className="space-y-5 lg:sticky lg:top-24">
              <QualityCard completeness={completeness} />
              <PremiumUpgradeCard
                isPremium={premium}
                profileType="Perfil profissional"
                profileName={formData.trade_name || effectiveProfile?.trade_name}
                email={effectiveProfile?.email}
                phone={formData.phone || effectiveProfile?.phone}
                cnpj={effectiveProfile?.cnpj}
                requestStatus={effectiveProfile?.premium_request_status}
                onCheckout={startPremiumCheckout}
              />
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function PublicPreview({ formData, profile, premium, completeness }) {
  const location = [formData.address, formData.neighborhood, formData.city, formData.state].filter(Boolean).join(' - ');

  return (
    <section className="overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-sm" data-testid="provider-public-preview">
      <div className="h-1.5 bg-gradient-to-r from-primary via-emerald-400 to-amber-300" />
      <div className="grid md:grid-cols-[1fr_280px]">
        <div className="p-5 md:p-6">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100 text-slate-300">
              {profilePhoto(profile) ? (
                <img src={photoUrl(profilePhoto(profile))} alt={formData.trade_name || 'Foto do perfil'} className="h-full w-full object-cover" />
              ) : (
                <UserRound className="h-10 w-10" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Prévia do perfil</Badge>
                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                  {completeness.percent}% completo
                </Badge>
              </div>
              <h2 className="text-2xl font-bold leading-tight text-slate-950">
                {formData.trade_name || 'Nome do perfil'}
              </h2>
              <p className="mt-1 font-medium text-primary">
                {formData.specialty_name || 'Especialidade'} {formData.city ? `em ${formData.city}` : ''}
              </p>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
                {formData.description || 'Adicione uma apresentação clara para explicar sua atuação, diferenciais e como você atende pacientes.'}
              </p>
              {(formData.formacao || formData.experiencia) && (
                <div className="mt-4 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                  {formData.formacao && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-3 py-1.5">
                      <GraduationCap className="h-3.5 w-3.5 text-primary" />
                      Formação informada
                    </span>
                  )}
                  {formData.experiencia && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-3 py-1.5">
                      <Briefcase className="h-3.5 w-3.5 text-primary" />
                      Experiência informada
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-emerald-100 bg-emerald-50/70 p-5 md:border-l md:border-t-0 md:p-6">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-primary">Atendimento</p>
          <div className="space-y-3 text-sm text-slate-700">
            <p className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>{location || 'Informe endereço, bairro, cidade e UF.'}</span>
            </p>
            <p className="flex items-center gap-2">
              <Phone className="h-4 w-4 shrink-0 text-primary" />
              <span>{formData.phone || 'Informe um telefone de contato.'}</span>
            </p>
            {premium ? (
              <p className="inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-1.5 text-xs font-semibold text-green-800">
                <MessageCircle className="h-3.5 w-3.5" />
                WhatsApp liberado
              </p>
            ) : (
              <p className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-600">
                <Shield className="h-3.5 w-3.5" />
                WhatsApp disponível no Premium
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function QualityCard({ completeness }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="provider-quality-card">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <TrendingUp className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-950">Qualidade do perfil</h3>
          <p className="text-xs text-slate-500">Quanto mais completo, maior a confiança.</p>
        </div>
      </div>

      <div className="mb-4">
        <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
          <span>Completude</span>
          <span className="font-semibold text-slate-800">{completeness.percent}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${completeness.percent}%` }} />
        </div>
      </div>

      <ul className="space-y-2.5">
        {completeness.items.map((item) => (
          <li key={item.key} className="flex items-start gap-2 text-sm">
            {item.done ? (
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            ) : (
              <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
            )}
            <span className={item.done ? 'text-slate-700' : 'text-slate-500'}>{item.label}</span>
          </li>
        ))}
      </ul>

      <div className="mt-4 rounded-xl bg-emerald-50 p-3 text-xs leading-relaxed text-emerald-900">
        <Search className="mr-1 inline h-3.5 w-3.5" />
        Perfis completos ficam mais preparados para busca interna, SEO e conversão.
      </div>
    </div>
  );
}
